import JSEncrypt from 'jsencrypt';
import md5 from 'js-md5';
import { encode, decode } from 'js-base64';
import CryptoJS from 'crypto-js';
import UUID from 'uuidjs';
import axios from 'axios';

var encrypt = {
    md5: {
        encrypt: (str) => md5(str),
    },
    rsa: {
        encrypt: (str, publickKey) => {
            const jsencrypt = new JSEncrypt({});
            jsencrypt.setPublicKey(publickKey);
            return jsencrypt.encrypt(str);
        },
        decrypt: (str, privateKey) => {
            const jsdecrypt = new JSEncrypt({});
            jsdecrypt.setPrivateKey(privateKey);
            return jsdecrypt.decrypt(str);
        },
    },
    base64: {
        encode: (str) => encode(str),
        decode: (str) => decode(str),
    },
    aes: {
        encrypt: (word, key = '') => {
            return CryptoJS.AES.encrypt(word, key).toString();
        },
        decrypt: (word, key = '') => {
            const bytes = CryptoJS.AES.decrypt(word, key);
            const originTxt = bytes.toString(CryptoJS.enc.Utf8);
            return originTxt;
        },
    },
};

function isNil(value) {
    return value === undefined || value === null;
}
function isNull(value) {
    return value === undefined || value === null;
}
function isNumber(value) {
    return (typeof value === 'number' ||
        Object.prototype.toString.call(value) === '[Object Number]');
}
function isObject(value) {
    const type = typeof value;
    return value != null && (type === 'object' || type === 'function');
}
function isFunction(value) {
    return typeof value === 'function';
}
function isNullOrEmpty(value) {
    if (isNull(value)) {
        return true;
    }
    switch (Object.prototype.toString.call(value)) {
        case '[object String]':
            return value.trim().length === 0;
        case '[object Array]':
            return value.length === 0;
        case '[object Object]':
            return Object.keys(value).length === 0;
        default:
            throw new TypeError();
    }
}

var common = /*#__PURE__*/Object.freeze({
    __proto__: null,
    isNil: isNil,
    isNull: isNull,
    isNumber: isNumber,
    isObject: isObject,
    isFunction: isFunction,
    isNullOrEmpty: isNullOrEmpty
});

/**
 * 创建一个 Guid
 */
function generate() {
    return UUID.generate();
}
/**
 * 判断 uuid 是否一致
 * @param {string} value1
 * @param {string} value2
 */
function isSame(value1, value2) {
    if (isNull(value1) || isNull(value2)) {
        return false;
    }
    return (value1.replace(/[{}]/g, '').toUpperCase() ===
        value2.replace(/[{}]/g, '').toUpperCase());
}
var uuid = {
    generate,
    isSame,
};

axios.defaults.timeout = 20000;
axios.defaults.withCredentials = true;
function _handleSuccess(res) {
    if (!res)
        return;
    if (!res.data) {
        return res.data;
    }
    if (res.data.ErrorCode === 0) {
        return res.data.Data || res.data;
    }
    else if (!isNullOrEmpty(res.data.ErrorCode) && !isNullOrEmpty(res.data.Message)) {
        return Promise.reject(new Error(res.data.Message));
    }
    else {
        return res.data;
    }
}
function getErrorMessage(error, defaultMessage = 'Oops！') {
    if (typeof error === 'string' && !isNullOrEmpty(error)) {
        return error;
    }
    if (error && error.Message) {
        return error.Message;
    }
    if (error && error.message) {
        return error.message;
    }
    return defaultMessage;
}
function _handleError(error) {
    const { status, data = {} } = error.response || {};
    let errorMessage;
    if (status === 403) {
        errorMessage = getErrorMessage(data, '您没有权限访问该资源');
    }
    else if (status === 401) {
        errorMessage = '请重新登录';
    }
    else if (status === 500) {
        errorMessage = getErrorMessage(data, '系统错误，请联系管理员');
    }
    else if (status === 404) {
        errorMessage = getErrorMessage(data.Message, '未找到资源');
    }
    else if (data.Message) {
        errorMessage = data.Message;
    }
    else if (error.message) {
        errorMessage = error.message;
    }
    else {
        errorMessage = '服务器开小差了';
    }
    return errorMessage;
}
var http = {
    get: (url, config) => {
        return new Promise((resolve, reject) => {
            axios
                .get(url, config)
                .then(res => resolve(_handleSuccess(res)))
                .catch(err => reject(_handleError(err)));
        });
    },
    post: (url, data, config) => {
        return new Promise((resolve, reject) => {
            axios
                .post(url, data, config)
                .then(res => resolve(_handleSuccess(res)))
                .catch(err => reject(_handleError(err)));
        });
    },
    delete: (url, config) => {
        return new Promise((resolve, reject) => {
            axios
                .delete(url, config)
                .then(res => resolve(_handleSuccess(res)))
                .catch(err => reject(_handleError(err)));
        });
    },
    put: (url, data, config) => {
        return new Promise((resolve, reject) => {
            axios
                .put(url, data, config)
                .then(res => resolve(_handleSuccess(res)))
                .catch(err => reject(_handleError(err)));
        });
    }
};

function init(url) {
    window.WebSocket = window.WebSocket || window.MozWebSocket;
    if (isNil(window.WebSocket)) {
        console.error('浏览器不支持WebSocket');
        throw new Error('浏览器不支持WebSocket');
    }
    return new window.WebSocket(url);
}
function Socket(url) {
    const client = init(url);
    return client;
}

/**
 * 原型链添加方法
 * @param {String} name
 * @param {Object} func
 * @returns this
 */
Function.prototype.method = function (name, func) {
    if (!this.prototype[name]) {
        this.prototype[name] = func;
    }
    return this;
};
String.prototype.toText = function () {
    if (!isNullOrEmpty(this)) {
        const result = this.replace(/<([^>]+)>([\d\D]*?)<\/\1>/g, '$2 ').split(/\s+/);
        result.pop(); // 去掉最后一个空格
        return result.join();
    }
    return '';
};
String.prototype.toBase64String = function () {
    if (!isNullOrEmpty(this)) {
        return encode(this.toString());
    }
    return '';
};
String.prototype.toStringFromBase64 = function () {
    if (!isNullOrEmpty(this)) {
        return decode(this.toString());
    }
    return '';
};
/**
 * 去除尾部字符空格
 * @param {String} c
 * @returns
 */
String.prototype.trimLast = function (c) {
    if (isNullOrEmpty(c)) {
        return String.prototype.trimEnd.call(this);
    }
    else {
        var str = this;
        var rg = new RegExp(c);
        var i = str.length;
        while (rg.test(str.charAt(--i)))
            ;
        return str.slice(0, i + 1);
    }
};
/**
 * 根据传入大小分组数组
 * @param {Number} size
 * @returns {Array} 返回分组后的数组
 */
Array.prototype.chunk = function (size = 1) {
    size = Math.max(parseInt(size.toString()), 0);
    const length = this == null ? 0 : this.length;
    if (!length || size < 1) {
        return [];
    }
    let index = 0;
    let resIndex = 0;
    const result = new Array(Math.ceil(length / size));
    while (index < length) {
        result[resIndex++] = this.slice(index, (index += size));
    }
    return result;
};
/**
 * 数组去重
 */
Array.prototype.distinct = function () {
    return [...new Set(this)];
};

function getLocationByWeixin() {
    return new Promise((resolve, reject) => {
        try {
            window.wx.getLocation({
                type: 'wgs84',
                success(res) {
                    resolve({
                        latitude: res.latitude,
                        longitude: res.longitude // 经度，浮点数，范围为180 ~ -180。
                    });
                },
                fail(error) {
                    reject(error);
                },
                cancel(error) {
                    reject(error);
                }
            });
        }
        catch (error) {
            reject(error);
        }
        setTimeout(reject, 3000);
    });
}
function getLocationByGeoAPI() {
    return new Promise((resolve, reject) => {
        try {
            navigator.geolocation.getCurrentPosition(position => {
                resolve({
                    latitude: position.coords.latitude,
                    longitude: position.coords.longitude
                });
            }, reject, {
                timeout: 3000
            });
        }
        catch (error) {
            reject(error);
        }
    });
}
function getLocation() {
    if (window.wx && window.wx.getLocation) {
        return getLocationByWeixin();
    }
    return getLocationByGeoAPI();
}
var location = {
    getLocationByGeoAPI,
    getLocationByWeixin,
    getLocation
};

export { Socket, common, encrypt, http, location, uuid };
